// ============================================================================
// StakeTek Concrete-Mount Base Bracket — SINGLE editable source of truth.
//
// NEW PRODUCT (scaffolded 2026-08-14). Lets a standard star picket / StakeTek
// system be installed onto concrete instead of being driven into soil: a base
// that mechanically fixes to a concrete slab and holds a star picket vertical.
//
// SALE-READY BUILD. The product page is a full Shopify buy flow — price panel,
// quantity, Add to cart and Buy now — identical to the bracket/extension pages.
// It ships at the same weight as (or lighter than) the bracket, so Shopify
// weight-based shipping already covers it. The page goes LIVE the moment two
// placeholders are filled: a confirmed `price` and a Shopify `shopifyProductId`
// (see concreteMountIsBuyable + the go-live checklist below). Until then the buy
// buttons are disabled with a clear dev note — NOT an enquiry form.
//
// DO NOT INVENT — every value below marked TBD is a real placeholder. Fill it
// in here (one file) and the product page, cards, nav and JSON-LD all follow.
// No engineering, load-rating or structural-certification claims anywhere: we
// have not provided them, so the copy stays descriptive only.
//
// Placeholders still required before pushing live (grouped for the interview):
//   • price               — [PRICE TBD]  ← blocks checkout
//   • Shopify product id   — [SHOPIFY LINK TBD]  ← blocks checkout
//   • dimensions           — [DIMENSIONS TBD] (base plate footprint, height)
//   • material thickness   — [THICKNESS TBD]
//   • anchor / fixing spec — [ANCHOR TBD] (bolt size, fixings per base)
//   • finish               — [FINISH TBD]
//   • included hardware    — [INCLUDED HARDWARE TBD]
//   • compatibility detail — [COMPATIBILITY TBD] (which pickets, slab min.)
//   • installation spec    — [INSTALL SPEC TBD]
//   • product photos/video — [PRODUCT IMAGE PLACEHOLDER] / [DEMO VIDEO TBD]
//   (Shopify id + price above are what actually gate checkout; the rest can be
//    filled in as they're confirmed without blocking the sale.)
// ============================================================================

export const CONCRETE_MOUNT = {
  // COMING SOON VENTURE (Tanner, 2026-08-21) — off the homepage until launch.
  // Renamed to ANCHOR Base Bracket: it anchors to any solid surface (concrete,
  // roofs, decks, hard ground), not just concrete. URL renamed while nothing is
  // indexed; the old /products/concrete-picket-base slug 301s in _redirects.
  key: "anchor-bracket",
  slug: "anchor-bracket",
  path: "/products/anchor-bracket",
  name: "StakeTek Anchor Base Bracket",
  shortName: "Anchor Base Bracket",
  productLabel: "STAKETEK · BRACKET RANGE",
  // Finish is TBD — do not invent. Left null so nothing false is displayed.
  material: "Steel", // generic, confirmed; grade/thickness TBD below
  finish: null, // [FINISH TBD]

  cardDescription:
    "Anchors a star picket to any solid surface — concrete, rooftops, decks or hard ground — so you can build where a picket can't be driven in.",
  tagline: "Build with star pickets, without being limited by the ground.",

  heroTitle: "StakeTek Anchor Base Bracket",
  heroDescription:
    "The StakeTek base bracket that mechanically anchors to any solid surface — a concrete slab, a rooftop, a deck or hard ground — and holds a standard star picket vertical, ready to build on with the rest of the StakeTek system.",
  supportingStatement:
    "Anchor a star picket to a solid surface instead of driving it into the ground, then build on it with StakeTek brackets and timber the same way you would on any StakeTek fence line.",

  // Positioning bullets — practical, no load/engineering claims.
  positioning: [
    "Anchors a star picket to concrete, roofs, decks or hard ground",
    "Mechanically fixed to the surface",
    "Works with the standard StakeTek slide-on bracket system",
    "For surfaces a picket can't be driven into",
    "Suited to shelving, barriers, workshop and garden builds",
    "Ships at bracket weight or lighter — standard shipping",
  ],

  // CONFIRMED specification rows only. Everything not yet confirmed is shown as
  // "To be confirmed" so the table has structure but states nothing false.
  specs: [
    ["Product type", "Anchor base bracket for star pickets"],
    ["Purpose", "Anchors a star picket to a solid surface — concrete, roof, deck or hard ground"],
    ["Installation", "Mechanically anchored to the surface"],
    ["Works with", "Standard star pickets and StakeTek slide-on brackets"],
    ["Base dimensions", "To be confirmed"], // [DIMENSIONS TBD]
    ["Material thickness", "To be confirmed"], // [THICKNESS TBD]
    ["Anchor / fixing requirement", "To be confirmed"], // [ANCHOR TBD]
    ["Finish", "To be confirmed"], // [FINISH TBD]
    ["Included hardware", "To be confirmed"], // [INCLUDED HARDWARE TBD]
  ],

  benefits: [
    {
      title: "Build where you can't dig",
      body: "Concrete slabs, rooftops, decks, workshop floors and paved areas — anywhere a star picket can't be driven into the ground.",
    },
    {
      title: "Same star-picket system",
      body: "Once the picket is mounted, build on it with StakeTek slide-on brackets and timber exactly as you would on any StakeTek fence line.",
    },
    {
      title: "Mechanically anchored",
      body: "The base anchors to the surface, so the picket stands vertical without being buried.",
    },
    {
      title: "Removable system",
      body: "Part of the StakeTek slide-on approach — built to be assembled and reconfigured, not fabricated in place.",
    },
  ],

  // Uses the customer can put it to. No structural / load-bearing guarantees.
  applications: [
    "Shelving",
    "Barriers",
    "Temporary structures",
    "Workshop builds",
    "Garden structures",
    "Rooftop and deck structures",
    "Fencing-related applications",
    "Star-picket structures where the ground can't be penetrated",
  ],

  // Kept honest and non-committal until confirmed — no slab-thickness or
  // load claims we haven't provided.
  compatibility:
    "Designed to anchor a standard star picket to a solid surface and work with the StakeTek slide-on bracket system. See the specification table for the confirmed detail on picket sizes, suitable surfaces and fixing requirements.", // [COMPATIBILITY TBD — fill the spec rows]

  // Two-part install overview — deliberately measurement-free until confirmed.
  installOverview: [
    {
      title: "Anchor the base to the surface",
      body: "Position the base on the slab, roof or ground and mechanically anchor it down with the specified fixings.", // [INSTALL SPEC TBD — confirm anchor type/count]
    },
    {
      title: "Mount the picket",
      body: "Set a standard star picket into the base so it stands vertical, ready to build on.",
    },
    {
      title: "Build with StakeTek hardware",
      body: "Slide StakeTek brackets onto the picket and bolt on timber, panel or sheet the same way you would on any StakeTek build.",
    },
  ],

  faqs: [
    {
      q: "What is the Anchor Base Bracket for?",
      a: "It lets you anchor a star picket to a solid surface — a concrete slab, a rooftop, a deck or hard ground — instead of driving it in, so you can build with the star-picket system almost anywhere.",
    },
    {
      q: "What can I build with it?",
      a: "It's intended for uses such as shelving, barriers, temporary structures, workshop and garden builds, and other star-picket structures where the ground can't be penetrated.",
    },
    {
      q: "Does it work with StakeTek brackets?",
      a: "Yes. Once a picket is mounted in the base, you build on it with the standard StakeTek slide-on bracket system, the same as on any StakeTek fence line.",
    },
    {
      q: "How does it fix to the surface?",
      a: "It's mechanically anchored — to concrete, roof structure, decking or other solid surfaces. The anchor and fixing detail is listed in the specification table on this page.",
    },
    {
      q: "How is it shipped?",
      a: "It ships Australia-wide the same way as our brackets — it's a similar weight or lighter, so standard weight-based shipping applies at checkout.",
    },
    {
      q: "Can I use it with StakeTek brackets and timber?",
      a: "Yes — that's the idea. Mount the picket in the base, then build on it with the standard StakeTek slide-on bracket system and your own timber, panel or sheet.",
    },
  ],

  crossLink: {
    text: "Ready to build once your picket is mounted?",
    label: "View the Supported Slide-On Bracket",
    to: "/products/supported-bracket",
  },

  // NO photos while it's a coming-soon venture (Tanner 2026-08-21: prefer a
  // clean "Coming soon" slot over the early shot). The processed photo is kept
  // on disk at /images/staketek-concrete-base-bracket-1.jpg — re-add it here at
  // launch and the product page, range card, Products index and related cards
  // all pick it up.
  images: [],
  // No extra note needed while the photo slot itself reads "Coming soon".
  morePhotosComing: false,

  // [SHOPIFY LINK TBD] — not connected to Shopify. Null keeps the page in the
  // "launching soon / register interest" state (concreteMountIsBuyable() → false).
  shopifyProductId: null,
  // [PRICE TBD] — no confirmed price. Never render a fabricated figure.
  price: null,
  weightGrams: null, // PENDING — set on the Shopify variant for shipping at go-live.

  seoTitle: "StakeTek Anchor Base Bracket | Mount Star Pickets to Any Surface",
  seoDescription:
    "Anchor a star picket to concrete, rooftops, decks or hard ground with the StakeTek Anchor Base Bracket — build with the star-picket system where a picket can't be driven in. Coming soon.",
  keywords:
    "star picket anchor bracket, anchor base bracket, mount star picket to concrete, star picket base plate, star picket on roof, star picket base bracket, bolt down star picket, star picket concrete base, star picket on slab, picket base bracket, star picket foot plate",
};

// Buyable (full Add-to-cart / Buy-now flow) once a live Shopify product id is
// set AND a confirmed price exists. Until both are filled the buy buttons render
// disabled with a dev note — the page is otherwise a complete store page.
export const concreteMountIsBuyable = () =>
  Boolean(CONCRETE_MOUNT.shopifyProductId) && CONCRETE_MOUNT.price != null;

// Customer-facing price line for cards (Products index etc.). Auto-updates the
// moment `price` is set.
export const concreteMountCardPrice = () =>
  CONCRETE_MOUNT.price != null ? `$${CONCRETE_MOUNT.price.toFixed(2)} each` : "New — price at launch"; // [PRICE TBD]

// ----------------------------------------------------------------------------
// SHOPIFY GO-LIVE CHECKLIST (Concrete-Mount Base Bracket)
// ----------------------------------------------------------------------------
// TO SELL (the two that gate checkout):
//   A. Set `price` above to the confirmed each price.
//   B. Create the Shopify product (single variant unless sizes are added), set
//      it ACTIVE + published to the Online Store channel, and paste its numeric
//      id into `shopifyProductId` above AND lib/shopify.js SHOPIFY_PRODUCTS
//      .concreteMount. Set a shipping WEIGHT on the variant (bracket weight or
//      lighter). concreteMountIsBuyable() then flips true and Add to cart / Buy
//      now go live. Verify the checkout price matches the site.
// TO FINISH THE PAGE (not blocking the sale):
//   C. Fill the TBD rows in `specs`, plus `finish`, and add product photos to
//      `images` (and swap the demo-video placeholder for the finished video).
